/**
 * Truth block — the Place Trust Report section of the verdict card, plus the
 * resident report form. This is the moment the map stops being a curated
 * opinion and shows the evidence layer: decision summary, per-pillar status,
 * coverage gaps, freshness, and the engine's own disclaimer.
 *
 * Footprint contract: engine-indexed cities (Sydney) reserve the block's
 * vertical slot with a fixed-height skeleton immediately, so the verdict
 * card never reflows mid-read when the report lands. Non-engine cities keep
 * zero footprint — no fake loading states for cities the engine doesn't
 * index. If the engine answers with nothing, the slot collapses to one
 * honest line rather than silently vanishing after a skeleton was shown.
 *
 * Framing rules enforced here (locked by the engine contract):
 *   partial_report      → headline is `score_label` text, score number muted
 *   substantial/full    → score number may lead, coverage caveat attached
 *   partial_live pillar → coverage_label + missing sub-signals rendered so a
 *                         score is never read as comprehensive
 *   not_yet_implemented → "not indexed yet" (muted), never a judgement
 */

import {
  loadTrustReport,
  cityFromVerdictId,
  isEngineCity,
  engineBase,
  type TrustReportLite,
  type TrustPillar,
} from "../data/trust-report-loader";

const STYLE_ID = "hyperlocal-truth-block-styles";

const PILLAR_LABELS: Array<[string, string]> = [
  ["safety", "Safety"],
  ["facilities", "Facilities"],
  ["growth", "Growth"],
  ["social_fabric", "Social fabric"],
  ["community", "Community"],
  ["builder", "Builder"],
];

const REPORT_CATEGORIES: Array<[string, string]> = [
  ["safety", "Safety"],
  ["civic_issues", "Civic issue"],
  ["infrastructure_works", "Infrastructure"],
  ["local_observations", "Local observation"],
  ["complaints", "Complaint"],
  ["events", "Event"],
  ["community_discussions", "Discussion"],
  ["other", "Other"],
];

/** Container chrome shared by skeleton and rendered states, so the swap
 *  changes content, not the frame. */
const BLOCK_CSS =
  "margin:0 0 10px 0;padding:10px;border:1px solid var(--hairline-2);" +
  "border-radius:var(--r-inner);background:var(--surface);";

function el(tag: string, css: string, text?: string): HTMLElement {
  const node = document.createElement(tag);
  node.style.cssText = css;
  if (text !== undefined) node.textContent = text;
  return node;
}

function eyebrow(): HTMLElement {
  return el(
    "div",
    "font-size:10px;letter-spacing:0.08em;text-transform:uppercase;color:var(--ink-3);margin-bottom:4px;",
    "Truth layer · place trust report",
  );
}

/* ------------------------------- SKELETON ------------------------------- */

function injectSkeletonStyles(): void {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement("style");
  s.id = STYLE_ID;
  // A shimmer is a loading state, not decoration; reduced-motion gets a
  // static placeholder. Nothing blinks — one slow breath.
  s.textContent = `
  .tb-skel-bar {
    height: 9px;
    border-radius: var(--r-chip);
    background: var(--hairline-2);
    margin: 11px 0;
    animation: tb-skel-breathe 1.8s var(--ease-out) infinite;
  }
  @keyframes tb-skel-breathe {
    0%, 100% { opacity: 0.5; }
    50% { opacity: 1; }
  }
  @media (prefers-reduced-motion: reduce) {
    .tb-skel-bar { animation: none; }
  }
  `;
  document.head.appendChild(s);
}

/** Fixed-height placeholder so the verdict card's layout is settled before
 *  the report lands. Height approximates the rendered block (lead + six
 *  pillar rows + freshness + form toggle). */
function renderSkeleton(container: HTMLElement): void {
  injectSkeletonStyles();
  container.innerHTML = "";
  container.style.cssText = BLOCK_CSS + "height:300px;overflow:hidden;box-sizing:border-box;";
  container.appendChild(eyebrow());
  const widths = [62, 88, 74, 70, 76, 68, 72, 66, 54];
  for (const w of widths) {
    const bar = el("div", `width:${w}%;`);
    bar.className = "tb-skel-bar";
    container.appendChild(bar);
  }
}

/** The engine answered with nothing (place unknown or unreachable). The
 *  reserved slot collapses to one honest line instead of vanishing. */
function renderUnavailable(container: HTMLElement): void {
  container.innerHTML = "";
  container.style.cssText = BLOCK_CSS;
  container.appendChild(eyebrow());
  container.appendChild(
    el("div", "font-size:11px;color:var(--ink-3);", "Trust report unavailable right now."),
  );
}

/* -------------------------------- RENDER -------------------------------- */

function pillarRows(label: string, p: TrustPillar): HTMLElement[] {
  const row = el(
    "div",
    "display:flex;justify-content:space-between;gap:8px;padding:2px 0;font-size:11.5px;",
  );
  row.appendChild(el("span", "color:var(--ink-2);", label));
  const right = el(
    "span",
    "color:var(--ink-3);text-align:right;font-variant-numeric:tabular-nums;",
  );
  if (p.status === "live" || p.status === "partial_live") {
    const score = p.score === null ? "—" : String(Math.round(p.score));
    right.textContent = p.status === "partial_live" && p.coverage_label
      ? `${score} · ${p.coverage_label}`
      : score;
    right.style.color = "var(--ink)";
  } else if (p.status === "not_applicable") {
    right.textContent = "n/a";
  } else {
    right.textContent = "not indexed yet";
  }
  row.appendChild(right);

  const rows = [row];
  // partial_live must never read as comprehensive — name what's missing.
  if (p.status === "partial_live" && p.missing_subsignals?.length) {
    rows.push(
      el(
        "div",
        "font-size:10.5px;color:var(--ink-3);padding:0 0 2px 0;text-align:right;",
        `not yet counted: ${p.missing_subsignals.map((s) => s.replace(/_/g, " ")).join(", ")}`,
      ),
    );
  }
  return rows;
}

/** Decision summary — the engine's calm lead, rendered only from fields the
 *  payload carries (wire shape: headline + bullets; body/string for compat). */
function decisionLead(ds: TrustReportLite["decision_summary"]): HTMLElement | null {
  if (!ds) return null;
  const wrap = el("div", "margin-bottom:8px;");
  if (typeof ds === "string") {
    wrap.appendChild(el("div", "font-size:12px;color:var(--ink);line-height:1.45;", ds));
    return wrap;
  }
  let any = false;
  if (ds.headline) {
    wrap.appendChild(
      el("div", "font-size:13px;font-weight:600;color:var(--ink);line-height:1.4;", ds.headline),
    );
    any = true;
  }
  if (ds.body) {
    wrap.appendChild(
      el("div", "font-size:11.5px;color:var(--ink-2);line-height:1.45;margin-top:2px;", ds.body),
    );
    any = true;
  }
  if (Array.isArray(ds.bullets)) {
    for (const b of ds.bullets) {
      if (typeof b !== "string" || !b) continue;
      wrap.appendChild(
        el("div", "font-size:11.5px;color:var(--ink-2);line-height:1.45;margin-top:2px;", `· ${b}`),
      );
      any = true;
    }
  }
  return any ? wrap : null;
}

function buildReportForm(verdictId: string): HTMLElement {
  const wrap = el("div", "margin-top:8px;");
  const toggle = el(
    "button",
    "background:none;border:1px solid var(--hairline-2);border-radius:var(--r-chip);" +
      "color:var(--blue);font-size:11px;padding:4px 10px;cursor:pointer;",
    "Report something here",
  );
  wrap.appendChild(toggle);

  let open = false;
  let form: HTMLElement | null = null;

  const fieldCss =
    "background:var(--bg-2);border:1px solid var(--hairline-2);border-radius:var(--r-chip);" +
    "color:var(--ink);font-size:11.5px;";

  toggle.addEventListener("click", () => {
    if (open && form) {
      form.remove();
      form = null;
      open = false;
      return;
    }
    open = true;
    form = el("div", "margin-top:8px;display:flex;flex-direction:column;gap:6px;");

    const select = document.createElement("select");
    select.style.cssText = fieldCss + "padding:4px;";
    for (const [val, label] of REPORT_CATEGORIES) {
      const opt = document.createElement("option");
      opt.value = val;
      opt.textContent = label;
      select.appendChild(opt);
    }

    const title = document.createElement("input");
    title.placeholder = "Short title (what happened?)";
    title.maxLength = 140;
    title.style.cssText = fieldCss + "padding:5px;";

    const body = document.createElement("textarea");
    body.placeholder = "What you observed, where exactly, and when (min 30 characters).";
    body.rows = 3;
    body.maxLength = 4000;
    body.style.cssText = fieldCss + "padding:5px;resize:vertical;";

    const status = el("div", "font-size:11px;color:var(--ink-3);min-height:14px;");
    const submit = el(
      "button",
      "background:var(--bg-2);border:1px solid var(--blue);border-radius:var(--r-chip);" +
        "color:var(--blue);font-size:11.5px;padding:6px 12px;cursor:pointer;align-self:flex-start;",
      "Submit for review",
    ) as HTMLButtonElement;

    submit.addEventListener("click", async () => {
      submit.disabled = true;
      status.textContent = "Submitting…";
      try {
        const res = await fetch(`${engineBase()}/reports`, {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            place: verdictId,
            city: cityFromVerdictId(verdictId),
            category: select.value,
            title: title.value.trim(),
            body: body.value.trim(),
          }),
        });
        const out = (await res.json()) as { ok?: boolean; errors?: string[]; note?: string };
        if (res.ok && out.ok) {
          status.style.color = "var(--trust-ok)";
          status.textContent =
            "Submitted. Verified reports become public evidence with full provenance.";
          title.value = "";
          body.value = "";
        } else {
          status.style.color = "var(--trust-disputed)";
          status.textContent = (out.errors ?? ["Submission failed — try again later."]).join(" · ");
          submit.disabled = false;
        }
      } catch {
        status.style.color = "var(--trust-disputed)";
        status.textContent = "Network error — try again later.";
        submit.disabled = false;
      }
    });

    form.appendChild(select);
    form.appendChild(title);
    form.appendChild(body);
    form.appendChild(submit);
    form.appendChild(status);
    wrap.appendChild(form);
  });

  return wrap;
}

function render(container: HTMLElement, verdictId: string, r: TrustReportLite): void {
  container.innerHTML = "";
  container.style.cssText = BLOCK_CSS;

  container.appendChild(eyebrow());

  // The engine's decision summary leads the block — the answer before the
  // numbers.
  const lead = decisionLead(r.decision_summary);
  if (lead) container.appendChild(lead);

  // Headline obeys the coverage framing contract.
  if (r.coverage_status === "partial_report") {
    container.appendChild(
      el("div", "font-size:13px;color:var(--ink);margin-bottom:2px;", r.score_label),
    );
    container.appendChild(
      el(
        "div",
        "font-size:11px;color:var(--ink-3);margin-bottom:6px;",
        `Early coverage — ${r.populated_pillars_count} of 6 pillars indexed. Scores stay muted until coverage grows.`,
      ),
    );
  } else {
    const score = r.trust_score === null ? "—" : String(Math.round(r.trust_score));
    container.appendChild(
      el("div", "font-size:15px;color:var(--ink);margin-bottom:2px;", `Trust score ${score} / 100`),
    );
    container.appendChild(
      el(
        "div",
        "font-size:11px;color:var(--ink-3);margin-bottom:6px;",
        `${r.score_label} · ${r.populated_pillars_count} of 6 pillars indexed` +
          (r.coverage_status === "substantial_report" ? " · coverage still growing" : ""),
      ),
    );
  }

  for (const [key, label] of PILLAR_LABELS) {
    const p = r.pillars[key];
    if (p) for (const row of pillarRows(label, p)) container.appendChild(row);
  }

  if (r.freshness?.display) {
    container.appendChild(
      el("div", "font-size:10.5px;color:var(--ink-3);margin-top:6px;", r.freshness.display),
    );
  }
  if (r.trust_summary) {
    container.appendChild(
      el("div", "font-size:11px;color:var(--ink-2);margin-top:6px;line-height:1.45;", r.trust_summary),
    );
  }

  container.appendChild(buildReportForm(verdictId));

  // The engine's own disclaimer closes the block — provenance is sacred.
  if (r.disclaimer) {
    container.appendChild(
      el(
        "div",
        "font-size:10.5px;color:var(--ink-3);margin-top:8px;line-height:1.45;",
        r.disclaimer,
      ),
    );
  }
}

/**
 * Build the truth block for a verdict. Engine cities get the reserved
 * skeleton slot immediately; other cities return an invisible container
 * that only appears if the engine (unexpectedly) answers for them.
 */
export function buildTruthBlock(verdictId: string, suburbName: string): HTMLElement {
  const container = document.createElement("div");
  const reserved = isEngineCity(cityFromVerdictId(verdictId));
  if (reserved) {
    renderSkeleton(container);
  } else {
    container.style.display = "none";
  }

  void loadTrustReport(verdictId, suburbName).then((r) => {
    if (!container.isConnected) return; // card already dismissed
    if (!r) {
      // Non-engine cities stay at zero footprint; a reserved slot says so.
      if (reserved) renderUnavailable(container);
      return;
    }
    container.style.display = "block";
    render(container, verdictId, r);
  });

  return container;
}
